import React from 'react';

const SpherePreview = ({ size = 500, color = '#FFFFFF', options = [] }) => {
  // Scale size for display (500-1500mm -> 120-300px height)
  const displayHeight = 120 + ((size - 500) / 1000) * 180;
  const displayWidth = displayHeight * 0.75; // Eggs are narrower than tall
  
  const hasHanging = options.includes('hanging');
  const isFlat = options.includes('flat');
  const hasRing = options.includes('ring');
  
  // Egg shape border radius
  const eggRadius = '50% 50% 50% 50% / 60% 60% 40% 40%';
  
  return (
    <div className="relative flex items-center justify-center w-full h-full min-h-[300px] md:min-h-[400px]">
      {/* Glow effect */}
      <div 
        className="absolute blur-3xl opacity-30"
        style={{
          width: displayWidth * 1.5,
          height: displayHeight * 1.5,
          backgroundColor: color,
          borderRadius: eggRadius,
        }}
      />
      
      {/* Hanging wire */}
      {hasHanging && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2">
          <div className="w-0.5 bg-zinc-500" style={{ height: `calc(50% - ${displayHeight/2}px + 20px)` }} />
          <div className="w-6 h-2 bg-zinc-600 rounded-sm mx-auto -mt-1" />
        </div>
      )}
      
      {/* Main egg container */}
      <div className="relative flex flex-col items-center" style={{ marginTop: hasHanging ? '40px' : '0' }}>
        <div className="flex flex-col items-center">
          {/* Main egg */}
          <div
            className="sphere-shadow transition-all duration-500 relative"
            style={{
              width: displayWidth,
              height: isFlat ? displayHeight * 0.92 : displayHeight,
              borderRadius: isFlat ? '50% 50% 45% 45% / 60% 60% 35% 35%' : eggRadius,
              background: `radial-gradient(ellipse at 30% 25%, ${color} 0%, ${adjustBrightness(color, -15)} 50%, ${adjustBrightness(color, -35)} 100%)`,
            }}
          >
            {/* Highlight */}
            <div 
              className="absolute top-[8%] left-[25%] w-[20%] h-[12%] bg-white/50 blur-sm"
              style={{ borderRadius: '50%' }}
            />
          </div>
          
          {/* Flat bottom plate indicator */}
          {isFlat && (
            <div 
              className="bg-zinc-600 rounded-sm -mt-1"
              style={{ width: displayWidth * 0.4, height: 6 }}
            />
          )}
        </div>
        
        {/* Ring base */}
        {hasRing && (
          <div className="relative mt-3" style={{ width: displayWidth * 0.8 }}>
            {/* Ring shadow */}
            <div 
              className="absolute top-1 left-1/2 -translate-x-1/2 bg-black/30 blur-sm rounded-full"
              style={{ 
                width: displayWidth * 0.75, 
                height: displayWidth * 0.2,
              }}
            />
            {/* Ring outer */}
            <div 
              className="relative mx-auto border-4 border-zinc-500 bg-zinc-700"
              style={{ 
                width: displayWidth * 0.8, 
                height: displayWidth * 0.22,
                borderRadius: '50%',
                transform: 'perspective(200px) rotateX(70deg)',
                boxShadow: 'inset 0 -4px 8px rgba(0,0,0,0.4), 0 4px 8px rgba(0,0,0,0.3)',
              }}
            />
            {/* Ring label */}
            <div className="text-center mt-2">
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Unterlegring</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Size indicator */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-center">
        <div className="text-xs text-zinc-500 uppercase tracking-widest mb-1">Höhe</div>
        <div className="text-2xl font-heading font-bold text-primary">{size} mm</div>
      </div>
    </div>
  );
};

// Helper function to adjust color brightness
function adjustBrightness(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, Math.min(255, (num >> 16) + amt));
  const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt));
  const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt));
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
}

export default SpherePreview;
